import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DndContextWrapper } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import theme from '@monorepo/shared/src/themes/defaultTheme';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import SVG from '@monorepo/shared/src/components/common/SVG';
import GWDragAndDrop from 'assets/images/drag-and-drop.png';
import {
  DndDragBox,
  DndDropBox,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import { ActivityContext } from 'state/providers/ActivityProvider';
import {
  useAudioPlayer,
  usePlaylist,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/AudioManager';
import type {
  GuessWhereRound,
  GuessWhereTile,
} from '@monorepo/shared/src/graph/schema-generated-types-student';
import { DndDragEndEvent, GuessStates } from 'types';
import GWhereRoundLayout from './GWhereRoundLayout';
import ActivityPlayButtonOverlay from '../../components/ActivityPlayButtonOverlay';
import clsx from 'clsx';
import AspectRatioWrapper from '@monorepo/shared/src/components/common/AspectRatioWrapper';
import { InstructionButtonContext } from 'state/providers/InstructionsButtonProvider';
import { getCorrectGuessStyle } from '../../util';
import sounds from '@monorepo/shared/src/transformers/genericSounds';
import { mapAssetsToSrcArray } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/AudioManager/utils';
interface Props {
  onRoundSuccess: () => void;
}

const FIRST_ROUND = 1;
const t = (key: string) => translate('activities', key);

const GWhereDndRound: React.FC<Props> = ({ onRoundSuccess }) => {
  const [{ currentAudioSrc, autoplayLocked, playing }, { play }] = useAudioPlayer();
  const [, { startPlaylist }] = usePlaylist();

  const [state, { hideTargetsFromScene, showSceneTarget, setIsActivityOver, setShowNextButton }] =
    useContext(ActivityContext);

  const sceneContainer = useRef<HTMLDivElement>(null);
  const svgWrapperRef = useRef<HTMLDivElement>(null);

  const [correctGuesses, setCorrectGuesses] = useState<Array<string>>([]);
  const [guessIndicator, setGuessIndicator] = useState(GuessStates.NULL);
  const [focusedTileId, setFocusedTileId] = useState<number | undefined>();
  const classes = styles();
  const currentRound: GuessWhereRound = state?.currentRound?.data;
  const [{ playInstructionFlag, instructionButtonDisabled }, { stopInstructions, setDisabled }] =
    useContext(InstructionButtonContext);

  useEffect(() => {
    playCurrentRoundQuestion();
  }, [currentRound.question]);

  useEffect(() => {
    const foundTile = currentRound.tiles?.find((t) => t.audio?.src === currentAudioSrc);
    setFocusedTileId(foundTile?.audio?.id);
  }, [currentAudioSrc]);

  const playAudioAndHighlightTiles = async () => {
    if (!currentRound?.tiles) {
      stopInstructions();
    }
    startPlaylist({
      sounds: mapAssetsToSrcArray(currentRound.tiles.map(({ audio }) => audio)),
      delay: 100,
      onEnd: () => {
        stopInstructions();
        setFocusedTileId(undefined);
      },
    });
  };

  const playCurrentRoundQuestion = async () =>
    startPlaylist({
      sounds: [currentRound.question.src, currentRound?.instructions?.src],
      onEnd: () => {
        currentRound?.tiles && playAudioAndHighlightTiles();
      },
    });

  useEffect(() => {
    if (playInstructionFlag && !instructionButtonDisabled) {
      playCurrentRoundQuestion();
    }
    return () => {
      setDisabled(false);
    };
  }, [playInstructionFlag]);

  const playAffirmation = async (tile: GuessWhereTile) => play(tile?.affirmation?.src || '');

  const playAnswer = async () => play(currentRound?.answer?.src || '');

  const handleOnDragEnd = (event: DndDragEndEvent) => {
    if (event.over && event.active.data?.current.image.correct) {
      onTileDropSuccess(event.active.data?.current);
    }

    if (event.over && !event.active.data?.current.image.correct) {
      onTileDropError();
    }
  };

  const handleOnSvgLoaded = () => {
    hideTargetsFromScene(sceneContainer);
  };

  const maybeEndActivity = async () => {
    if (state.isLastRound) {
      await setIsActivityOver(true);
      setShowNextButton(true);
      return;
    }
  };

  const onTileDropSuccess = async (tile: GuessWhereTile) => {
    setDisabled(true);
    setCorrectGuesses((currentState) => [tile.image.description, ...currentState]);
    setGuessIndicator(GuessStates.SUCCESS);
    setTimeout(() => {
      setGuessIndicator(GuessStates.NULL);
      showSceneTarget(
        sceneContainer,
        `#${tile.image.elementId?.toLowerCase()}`,
        state?.currentRound?.round == FIRST_ROUND ? undefined : classes.animatedItem
      );
    }, 500);
    await playAffirmation(tile);
    const currentCorrectGuesses = [tile.image.description, ...correctGuesses];
    const allCorrect = !state?.currentRound?.data?.tiles?.some(
      (x) => x.image.correct && !currentCorrectGuesses.includes(x.image.description)
    );
    if (allCorrect) {
      await playAnswer();
      setTimeout(() => {
        onRoundSuccess();
        maybeEndActivity();
      }, 2000);
    }
    setDisabled(false);
  };

  const onTileDropError = () => {
    if (playing) return;
    play(sounds.BOINK.src.src);
    setGuessIndicator(GuessStates.ERROR);
    setTimeout(() => {
      setGuessIndicator(GuessStates.NULL);
    }, 500);
  };

  const SceneComponent = useCallback(
    () => (
      <div className={classes.DropBoxContainer}>
        <AspectRatioWrapper ratio={936 / 366}>
          <DndDropBox
            styles={{
              container: { height: '100%', width: '100%' },
              refDiv: { height: '100%', width: '100%' },
            }}
          >
            <Paper className={classes.dndDropBox} elevation={4}>
              {correctGuesses.length > 0 ? (
                <div
                  className={clsx(
                    classes.sceneContainer,
                    state?.currentRound?.round == FIRST_ROUND && classes.animatedItem
                  )}
                  ref={sceneContainer}
                >
                  <SVG
                    onLoaded={handleOnSvgLoaded}
                    parentRef={sceneContainer}
                    svgSrc={state?.activity?.scene?.src}
                    svgWrapperRef={svgWrapperRef}
                  />
                </div>
              ) : (
                <img alt={t('gwDragAndDrop')} src={GWDragAndDrop} />
              )}
            </Paper>
          </DndDropBox>
        </AspectRatioWrapper>
      </div>
    ),
    [correctGuesses.length > 0]
  );

  const tileComponents = useMemo(
    () =>
      (state?.currentRound?.data?.tiles || []).map((tile) => () => (
        <DndDragBox
          data={tile}
          key={tile.image.description}
          id={tile.image.description}
          isCorrectStyle={getCorrectGuessStyle(correctGuesses, tile.image.description)}
        >
          <Paper
            className={classes.tileBackground}
            elevation={4}
            style={{
              border:
                focusedTileId === tile.audio?.id ? `solid ${theme.palette.grey[900]} 3px` : 'none',
            }}
          >
            <img className={classes.tileImage} src={tile.image.src} alt={tile.audio?.description} />
          </Paper>
        </DndDragBox>
      )),
    [state?.currentRound?.round, correctGuesses.length, focusedTileId]
  );

  return (
    <DndContextWrapper handleOnDragEnd={handleOnDragEnd as any}>
      <GWhereRoundLayout
        guessIndicator={guessIndicator}
        SceneComponent={<SceneComponent />}
        tileComponents={tileComponents}
      />
      {autoplayLocked && <ActivityPlayButtonOverlay />}
    </DndContextWrapper>
  );
};

const styles = makeStyles(() => ({
  '@keyframes brighten': {
    '0%': {
      filter: 'brightness(3)',
    },
    '100%': {
      filter: 'brightness(1)',
    },
  },
  animatedItem: {
    animation: `$brighten 2s forwards`,
  },
  dndDropBox: {
    alignItems: 'center',
    background: '#FBFBFB',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  sceneContainer: {
    height: '100%',
    width: '100%',
    '& > div': {
      height: '100%',
      width: '100%',
    },
    '& > div > svg': {
      height: '100%',
      width: '100%',
      objectFit: 'contain',
    },
  },
  tileBackground: {
    backgroundSize: 'cover',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tileImage: {
    height: '100%',
    width: '100%',
  },
  DropBoxContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default GWhereDndRound;
